import { CustomMeetYourHerbsState } from "@gaiaProfessionals/Store/MeetYourHerbs/GAIAMeetYourHerbsReducer";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import { WidgetGroup } from "@insite/client-framework/Types/WidgetGroups";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Accordion from "@insite/mobius/Accordion";
import AccordionSection, { AccordionSectionPresentationProps } from "@insite/mobius/AccordionSection";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useState } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: CustomMeetYourHerbsState) => {
    const product = state.customMeetYourHerbsReducer?.product;
    return {
        product,
        integrityTest: product?.ingredients,
    };
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps>;

export interface GAIAMYHIngredientAccordianStyles {
    mainWrapper?: InjectableCss;
    accordianMainWrapper?: InjectableCss;
    accordianWrapper?: AccordionSectionPresentationProps;
    accordianSectionTitle?: TypographyProps;
    accordianContentWrapper?: InjectableCss;
    imageContentWrapper?: InjectableCss;
    blackPaperTitle?: TypographyProps;
    blackPaperConclusion?: TypographyProps;
    image?: LazyImageProps;
    rowWrapper?: InjectableCss;
    columnWrapper?: InjectableCss;
    title?: TypographyProps;
    labelText?: TypographyProps;
    valueText?: TypographyProps;
    boldText?: TypographyProps;
}
const styles: GAIAMYHIngredientAccordianStyles = {
    mainWrapper: {
        css: css`
            padding: 53px 15px;
            background: #f3efeb;
        `,
    },
    title: {
        variant: "h2",
        color: "#183028",
        size: 28,
        css: css`
            margin-bottom: 25px;
        `,
    },
    accordianMainWrapper: {
        css: css`
            margin: 26px 0 20px;
        `,
    },
    accordianWrapper: {
        headerProps: {
            css: css`
                background: transparent;

                button {
                    border-radius: 4px;
                    border: none;
                    padding: 15px 20px;
                    background: #fff;
                    &:focus {
                        border: 0 solid transparent;
                        padding: 15px 20px;
                    }
                }
                button[aria-expanded="true"] {
                    outline-color: #09f;
                    outline-style: solid;
                    outline-width: 2px;
                    background: #c4d6a4;
                }
            `,
        },
        panelProps: {
            css: css`
                padding: 20px;
                background: #fff;
                border: none;
            `,
        },
    },
    accordianContentWrapper: {
        css: css``,
    },
    imageContentWrapper: {
        css: css`
            display: flex;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-direction: column;
                    `,
                    css`
                        flex-direction: column;
                    `,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    image: {
        css: css`
            max-width: 261px;
            width: 100%;
            height: 220px;
            margin-right: 30px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        height: 100%;
                    `,
                    css`
                        height: 100%;
                    `,
                    css`
                        height: 100px;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    blackPaperTitle: {
        css: css`
            font-family: LibreBaskerville;
            font-size: 18px;
            margin-bottom: 15px;
        `,
    },
    blackPaperConclusion: {
        css: css`
            font-family: Lato;
            font-size: 15px;
            margin-bottom: 18px;
        `,
    },
    accordianSectionTitle: {
        variant: "p",
        css: css`
            font-family: LatoBold;
            margin-bottom: 0;
            text-transform: capitalise;
            > span {
                font-family: Lato;
                margin-left: 5px;
            }
        `,
    },
    rowWrapper: {
        css: css`
            display: flex;
            column-gap: 20px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-direction: column;
                    `,
                    css`
                        flex-direction: column;
                    `,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    columnWrapper: {
        css: css`
            flex: 50%;
            padding: 13px 20px;
            background: #f0f4f3;
            margin-top: 19px;
        `,
    },
    labelText: {
        size: 15,
        fontFamily: "LatoMedium",
        css: css`
            margin-right: 5%;
            min-width: 175px;
            text-transform: capitalize;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        min-width: 50%;
                        max-width: 50%;
                    `,
                    css`
                        min-width: 50%;
                        max-width: 50%;
                    `,
                    css`
                        min-width: 125px;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    valueText: {
        size: 15,
        fontFamily: "Lato",
        css: css`
            display: flex;
            padding: 8px 0px;
            border-bottom: 1px solid #ccc;
            &:last-child {
                border-bottom: none;
            }
        `,
    },
    boldText: {
        fontFamily: "LatoBold",
        css: css`
            margin-bottom: 10px;
            display: block;
        `,
    },
};

const GAIAMYHIngredientAccordian: React.FC<Props> = ({ product, integrityTest }) => {
    const [isOpen, setIsOpen] = useState<number | undefined>(0);
    if (!integrityTest) {
        return null;
    }
    const ingredient = Object.keys(integrityTest);

    return (
        <StyledWrapper {...styles.mainWrapper}>
            <Typography {...styles.title}>Ingredients</Typography>
            <Typography>
                Our scientists begin by validating each ingredient at the raw material stage. We go beyond organoleptic
                testing and employ industry-leading methods to ensure the purity and integrity of each herb. It’s part
                of the Gaia difference. Find out more about each individual herb below or view the Complete Certificate
                for comprehensive validation of this batch.
            </Typography>
            {ingredient.map((label, index) => (
                <StyledWrapper {...styles.accordianMainWrapper} key={label}>
                    <Accordion headingLevel={3}>
                        <AccordionSection
                            title={
                                <Typography {...styles.accordianSectionTitle}>
                                    {integrityTest[label].integrity_commonnames}
                                </Typography>
                            }
                            {...styles.accordianWrapper}
                            expanded={index === isOpen}
                            onTogglePanel={() => {
                                if (index === isOpen) {
                                    setIsOpen(undefined);
                                } else {
                                    setIsOpen(index);
                                }
                            }}
                        >
                            <StyledWrapper {...styles.accordianContentWrapper}>
                                <StyledWrapper {...styles.imageContentWrapper}>
                                    <LazyImage src={product?.sellable_item["0"].image_3} {...styles.image} />
                                    <StyledWrapper>
                                        <Typography {...styles.blackPaperTitle} as="p">
                                            {integrityTest[label].cms?.name}
                                        </Typography>
                                        <Typography {...styles.blackPaperConclusion} as="p">
                                            The dried fruit of Black Pepper (Piper nigrum) has long been used as a
                                            culinary spice and as a traditional medicine, and today it is ubiquitous in
                                            most cuisines. Black Pepper is made from the plant’s unripened green drupes
                                            (stone fruit), which are called “peppercorns.” They are briefly boiled and
                                            dried or cooked. Native to southern and southeast Asia, Black Pepper’s use
                                            in Indian cooking dates to the first century BC, and it became popular
                                            across Europe during the Roman Empire. In ancient Gre...
                                        </Typography>
                                        <Link href="">Learn More</Link>
                                    </StyledWrapper>
                                </StyledWrapper>
                                <StyledWrapper {...styles.rowWrapper}>
                                    <StyledWrapper {...styles.columnWrapper}>
                                        <Typography {...styles.boldText}>Herb Batch A</Typography>
                                        <Typography {...styles.valueText} as="p">
                                            <Typography {...styles.labelText} as="p">
                                                {translate("herbs")}
                                            </Typography>
                                            <Typography>
                                                Country of Origin{integrityTest[label].integrity_CountryOfOrigin}
                                            </Typography>
                                        </Typography>

                                        <Typography {...styles.valueText} as="p">
                                            <Typography {...styles.labelText}>{translate("Biomarker")}</Typography>
                                            <Typography></Typography>
                                        </Typography>
                                        <Typography {...styles.valueText} as="p">
                                            <Typography {...styles.labelText} as="p">
                                                {translate("harvest")}
                                            </Typography>
                                            <Typography>{integrityTest[label].integrity_harvesttype}</Typography>
                                        </Typography>
                                        <Typography {...styles.valueText} as="p">
                                            <Typography {...styles.labelText} as="p">
                                                {translate("plant part used")}
                                            </Typography>
                                            <Typography>{integrityTest[label].plant_part}</Typography>
                                        </Typography>
                                        <Typography {...styles.valueText} as="p">
                                            <Typography {...styles.labelText} as="p">
                                                {translate("farm")}
                                            </Typography>
                                            <Typography>{integrityTest[label].integrity_vendorclass}</Typography>
                                        </Typography>
                                    </StyledWrapper>
                                    <StyledWrapper {...styles.columnWrapper}>
                                        <Typography {...styles.boldText}>Herb Batch B</Typography>
                                        <Typography {...styles.valueText} as="p">
                                            <Typography {...styles.labelText} as="p">
                                                {translate("herbs")}
                                            </Typography>
                                            <Typography>
                                                Country of Origin{integrityTest[label].integrity_CountryOfOrigin}
                                            </Typography>
                                        </Typography>

                                        <Typography {...styles.valueText} as="p">
                                            <Typography {...styles.labelText}>{translate("Biomarker")}</Typography>
                                            <Typography></Typography>
                                        </Typography>
                                        <Typography {...styles.valueText} as="p">
                                            <Typography {...styles.labelText} as="p">
                                                {translate("harvest")}
                                            </Typography>
                                            <Typography>{integrityTest[label].integrity_harvesttype}</Typography>
                                        </Typography>
                                        <Typography {...styles.valueText} as="p">
                                            <Typography {...styles.labelText} as="p">
                                                {translate("plant part used")}
                                            </Typography>
                                            <Typography>{integrityTest[label].plant_part}</Typography>
                                        </Typography>
                                        <Typography {...styles.valueText} as="p">
                                            <Typography {...styles.labelText} as="p">
                                                {translate("farm")}
                                            </Typography>
                                            <Typography>{integrityTest[label].integrity_vendorclass}</Typography>
                                        </Typography>
                                    </StyledWrapper>
                                </StyledWrapper>
                            </StyledWrapper>
                        </AccordionSection>
                    </Accordion>
                </StyledWrapper>
            ))}
        </StyledWrapper>
    );
};
const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(GAIAMYHIngredientAccordian),
    definition: {
        displayName: "MYH Ingredient Accordian",
        group: "MYH Product Details" as WidgetGroup,
    },
};
export default widgetModule;
